/**
 * Decides whether a bundle member row's cell must render blank.
 *
 * Consumed by `ArCell`, which renders an empty `role="cell"` when this returns true —
 * the column layout is unchanged, only the cell content is dropped — and by
 * `resolveArTableBundleExportVisibility`, which asks the same question per
 * (row, column) so both XLSX producers blank exactly the cells the grid blanks.
 * Opt-in hide-list per bundle type: a member cell is blank only when the column's
 * list for that bundle type names the active visibility mode. Absent or empty lists
 * never suppress, `internal` is always exempt, and non-member rows are unaffected.
 * An unresolvable `bundleType` (member whose header is absent from the row set)
 * fails closed: it is blank when either hide list names the mode. An unconfigured
 * column has no hide lists, so this is inert unless an operator asked for the
 * column to be withheld — and there, leaking is the costlier direction.
 */

import type { ArBundleType, ArColumnDefinition } from '#types/System.Artifacts.types';
import type { VisibilityTypeId } from '#types/System.types';

export function isBundleMemberCellSuppressed(
  bundleRole: 'member' | undefined,
  bundleType: ArBundleType | undefined,
  visibilityMode: VisibilityTypeId | undefined,
  column: Pick<ArColumnDefinition, 'kitMemberHiddenTypes' | 'groupMemberHiddenTypes'>,
): boolean {
  if (bundleRole !== 'member') return false;
  if (!visibilityMode || visibilityMode === 'internal') return false;
  if (bundleType === 'kit') return column.kitMemberHiddenTypes?.includes(visibilityMode) === true;
  if (bundleType === 'group') return column.groupMemberHiddenTypes?.includes(visibilityMode) === true;
  return column.kitMemberHiddenTypes?.includes(visibilityMode) === true
    || column.groupMemberHiddenTypes?.includes(visibilityMode) === true;
}
